import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

interface PrintPopupPortalProps {
  /** An already-`window.open()`-ed window — opened by the caller's own click handler (not here),
   * so the popup isn't blocked: browsers only allow `window.open` calls made synchronously
   * within a user gesture. */
  popupWindow: Window;
  /** Called once the popup is gone (closed by the user, or by a later print request replacing
   * it) — the caller should stop rendering this component in response. */
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Renders `children` into a separate browser tab/window instead of the current one, then
 * triggers that window's own `print()`. This isolates `window.print()`'s native print-preview
 * dialog to a disposable tab: Chromium has a known renderer-crash bug where clicking the browser
 * back button while that dialog is opening/closing can kill the tab
 * (https://issues.chromium.org/issues/442105456). If it happens here, only this popup is lost —
 * not the user's actual Trackly tab and its in-progress work.
 *
 * Uses an Emotion cache scoped to the popup's own `<head>` (`@emotion/cache`'s `createCache`) so
 * MUI's `sx`-based styling — which by default targets the *current* document — resolves
 * correctly in a different window's document too, letting the caller reuse existing MUI
 * components unchanged rather than hand-duplicating their styles as plain CSS.
 */
export function PrintPopupPortal({ popupWindow, onClose, children }: PrintPopupPortalProps) {
  const cache = useMemo(
    () => createCache({ key: 'print-popup', container: popupWindow.document.head }),
    [popupWindow]
  );

  useEffect(() => {
    // One frame so the portal's content (e.g. QR code SVGs) has actually painted before this
    // window's own print() runs — printing immediately on mount would print a blank page.
    const raf = popupWindow.requestAnimationFrame(() => popupWindow.print());
    popupWindow.addEventListener('pagehide', onClose);

    return () => {
      popupWindow.cancelAnimationFrame(raf);
      popupWindow.removeEventListener('pagehide', onClose);
      if (!popupWindow.closed) popupWindow.close();
    };
  }, [popupWindow, onClose]);

  return createPortal(<CacheProvider value={cache}>{children}</CacheProvider>, popupWindow.document.body);
}

export default PrintPopupPortal;
